import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Logo } from '../ui/Logo';
import { UserMenu } from './UserMenu';
import { CreditsBadge } from '../Credits/CreditsBadge';
import { Menu, X } from 'lucide-react';

interface AppHeaderProps {
  /** Extra content on the right side, before CreditsBadge */
  rightExtra?: React.ReactNode;
  /** Mobile menu toggle callback (for sidebar layouts) */
  onMobileMenuToggle?: () => void;
  /** Whether mobile menu is open (for sidebar layouts) */
  mobileMenuOpen?: boolean;
}

const navLinks = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'Templates', href: '/templates' },
];

/**
 * Shared top header used across all authenticated pages.
 * Shows: Logo | Dashboard | Templates | ... | CreditsBadge | UserMenu
 */
export function AppHeader({ rightExtra, onMobileMenuToggle, mobileMenuOpen }: AppHeaderProps) {
  const { pathname } = useLocation();

  return (
    <header className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-4 shrink-0 z-40 sticky" style={{ top: 'var(--verification-banner-h, 0px)' }}>
      <div className="flex items-center gap-3">
        {/* Mobile menu toggle for sidebar layouts */}
        {onMobileMenuToggle && (
          <button
            onClick={onMobileMenuToggle}
            className="md:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        )}

        <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
          <Logo size="md" />
        </Link>

        <nav className="hidden sm:flex items-center gap-1 ml-3">
          {navLinks.map(({ label, href }) => {
            const isActive = pathname === href || pathname.startsWith(href + '/');
            return isActive ? (
              <span
                key={href}
                className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md"
                aria-current="page"
              >
                {label}
              </span>
            ) : (
              <Link
                key={href}
                to={href}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800 rounded-md hover:bg-gray-100 transition-colors"
              >
                {label}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-3">
        {rightExtra}
        <CreditsBadge />
        <UserMenu />
      </div>
    </header>
  );
}
